/**
 * Administrative fee – auto add/remove based on subtotal
 * Optimized version – no fetch interception, uses Shopify events + Section Rendering API
 */
(function() {
  // CONFIG
  const FEE_SKU = '10992846045451';
  const SUBTOTAL_THRESHOLD = 1500; // 15.00 €

  let isProcessing = false;
  let feeVariantId = null;
  let lastSubtotal = -1;
  let lastFeeExists = false;
  let pendingCheck = null;

  function getVariantIdBySku(sku) {
    return fetch('/products/order-fees.js')
      .then(res => {
        if (!res.ok) throw new Error('Produktas nerastas');
        return res.json();
      })
      .then(product => {
        const variant = product.variants.find(v => v.sku === sku);
        if (!variant) throw new Error('SKU nerastas');
        return variant.id;
      });
  }

  function getFeeItem(cart) {
    if (!cart || !cart.items || !feeVariantId) return null;
    return cart.items.find(item => item.variant_id === feeVariantId) || null;
  }

  function hasOtherItems(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item => item.variant_id !== feeVariantId);
  }

  function getSubtotalWithoutFee(cart) {
    if (!cart || !cart.items) return 0;
    let subtotal = 0;
    for (let item of cart.items) {
      if (item.variant_id !== feeVariantId) {
        subtotal += item.final_line_price || (item.price * item.quantity);
      }
    }
    return subtotal;
  }

  // Prideda mokestį
  function addFee() {
    if (isProcessing || !feeVariantId) return;
    isProcessing = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: feeVariantId, quantity: 1 })
    })
    .then(res => res.json())
    .then(() => {
      isProcessing = false;
      refreshCartUI();
    })
    .catch(err => {
      console.error('❌ Klaida pridedant mokestį:', err);
      isProcessing = false;
      refreshCartUI();
    });
  }

  // Pašalina mokestį
  function removeFee() {
    if (isProcessing || !feeVariantId) return;
    isProcessing = true;

    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const index = cart.items.findIndex(item => item.variant_id === feeVariantId);
        if (index === -1) {
          isProcessing = false;
          return;
        }
        const lineIndex = index + 1;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: 0 })
        });
      })
      .then(() => {
        isProcessing = false;
        console.log('✅ Mokestis pašalintas');
        refreshCartUI();
      })
      .catch(err => {
        console.error('❌ Klaida šalinant mokestį:', err);
        isProcessing = false;
        refreshCartUI();
      });
  }

  // Atnaujina krepšelio UI naudojant Shopify Section Rendering API
  function refreshCartUI() {
    // 1. Atnaujiname krepšelio skaičių
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const count = cart.item_count;
        document.querySelectorAll('.cart-count, .header__cart-count').forEach(el => {
          el.textContent = count;
        });
      })
      .catch(() => {});

    // 2. Naudojame Shopify Section Rendering API – tikriname, ar yra sekcijos
    const sectionsToUpdate = ['main-cart', 'mini-cart'];
    const existingSections = sectionsToUpdate.filter(id => 
      document.querySelector(`[data-section-id="${id}"]`)
    );

    if (existingSections.length === 0) {
      // Jei nėra krepšelio sekcijų puslapyje – galime saugiai perkrauti
      console.log('🔄 Krepšelio sekcijų nėra, perkrauname puslapį');
      window.location.reload();
      return;
    }

    // Siunčiame užklausą su visomis sekcijomis
    const sectionParam = existingSections.join(',');
    fetch(`/?sections=${sectionParam}`)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        for (let sectionId of existingSections) {
          const oldSection = document.querySelector(`[data-section-id="${sectionId}"]`);
          const newSection = doc.querySelector(`[data-section-id="${sectionId}"]`);
          if (oldSection && newSection) {
            // Pakeičiame tik turinį, o ne patį elementą, kad neišnyktų event listeneriai
            oldSection.innerHTML = newSection.innerHTML;
            // Perkopijuojame data atributus, jei reikia
            for (let attr of newSection.attributes) {
              if (attr.name.startsWith('data-')) {
                oldSection.setAttribute(attr.name, attr.value);
              }
            }
            console.log(`🔄 Sekcija ${sectionId} atnaujinta (innerHTML)`);
          }
        }

        // Papildomai siunčiame eventą, kad tema atsinaujintų
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(err => {
        console.warn('⚠️ Klaida atnaujinant sekcijas:', err);
        // Jei nepavyksta – perkrauname puslapį
        window.location.reload();
      });
  }

  // Pagrindinė logika
  function checkAndUpdateFee(force = false) {
    if (isProcessing || !feeVariantId) return;

    if (pendingCheck) {
      clearTimeout(pendingCheck);
      pendingCheck = null;
    }

    pendingCheck = setTimeout(() => {
      pendingCheck = null;

      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const hasOther = hasOtherItems(cart);
          const subtotal = getSubtotalWithoutFee(cart);
          const feeExists = !!getFeeItem(cart);

          if (!hasOther) {
            if (feeExists) {
              console.log('🧹 Krepšelyje nėra kitų prekių, šaliname mokestį');
              removeFee();
            } else {
              console.log('⏸️ Krepšelis tuščias, nieko nedarome');
            }
            return;
          }

          // Jei būsena nepasikeitė ir nėra force – grįžtame
          if (!force && subtotal === lastSubtotal && feeExists === lastFeeExists) {
            return;
          }

          lastSubtotal = subtotal;
          lastFeeExists = feeExists;

          console.log(`🔍 Subtotal: ${(subtotal/100).toFixed(2)}€, Mokestis: ${feeExists ? 'yra' : 'nėra'}`);

          if (subtotal < SUBTOTAL_THRESHOLD && !feeExists) {
            console.log('➕ Pridedame mokestį');
            addFee();
          } else if (subtotal >= SUBTOTAL_THRESHOLD && feeExists) {
            console.log('➖ Šaliname mokestį');
            removeFee();
          }
        })
        .catch(err => console.error('❌ Klaida tikrinant krepšelį:', err));
    }, 300); // šiek tiek ilgesnis vėlavimas, kad išvengtume konfliktų
  }

  // -------------------------------
  // SHOPIFY EVENT LISTENERS (pagrindinis būdas)
  // -------------------------------
  function setupEventListeners() {
    // Dažniausiai naudojami Shopify 2.0 įvykiai
    const events = [
      'cart:updated',
      'cart:refresh',
      'product:added',
      'cart-drawer:updated',
      'section:reload'
    ];

    events.forEach(event => {
      document.addEventListener(event, () => {
        console.log(`📡 Gautas įvykis: ${event}`);
        setTimeout(() => checkAndUpdateFee(true), 300);
      });
    });

    // Papildomai – jei tema naudoja savo AJAX, bet neišmeta eventų
    // Stebime mygtukų paspaudimus
    document.addEventListener('click', function(e) {
      const target = e.target.closest('[data-action="decrease-quantity"], [data-action="increase-quantity"], [data-action="add-to-cart"]');
      if (target) {
        setTimeout(() => checkAndUpdateFee(true), 500);
      }
    });
  }

  // -------------------------------
  // POLLING – atsarginis variantas (kas 3 sek.)
  // -------------------------------
  let checkInterval = null;

  function startPolling() {
    if (checkInterval) clearInterval(checkInterval);
    checkInterval = setInterval(() => {
      checkAndUpdateFee(false);
    }, 3000);
  }

  // -------------------------------
  // INICIJAVIMAS
  // -------------------------------
  function init() {
    getVariantIdBySku(FEE_SKU)
      .then(id => {
        feeVariantId = id;
        console.log('✅ Administravimo mokestis: rastas variantas ID', id);
        setupEventListeners();
        setTimeout(() => checkAndUpdateFee(true), 500);
        startPolling(); // atsarginis variantas
      })
      .catch(err => {
        console.error('❌ Nepavyko rasti varianto:', err);
      });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function() {
    setTimeout(() => checkAndUpdateFee(true), 300);
  });

  window.updateAdminFee = checkAndUpdateFee;
})();